import * as React from 'react';
import { useQuery } from 'react-query';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { CreateEditingStyleInput, EditingStyle, UpdateEditingStyleInput } from './types';
import { useTelemetryContext } from '../../telemetry/telemetryContext';
import { getUserInfo } from '../../telemetry/utils';
import { useUserContext } from '../../shared/context/user-context';
import { EditingStylesApi } from './EditingStylesApi';
import { ListEditingStylesQueryKey } from './queryKeys';
import { EditingStylesList } from './list-editing-styles/view/EditingStylesList';
import { EditEditingStyleModal } from './edit-editing-styles/view/EditEditingStyleModal';
import { useEditEditingStyleModal } from './edit-editing-styles/state/useEditEditingStyleModal';
import {
    UpdateEditingStyleFailure,
    UpdateEditingStyleSuccess,
    useUpdateEditingStyle
} from './edit-editing-styles/state/useUpdateEditingStyle';
import { updateErrorText } from './edit-editing-styles/state/updateErrorText';
import { CreateEditingStyleModal } from './create-editing-style/view/CreateEditingStyleModal';
import { useCreateEditingStyleModal } from './create-editing-style/state/useCreateEditingStyleModal';
import {
    CreateEditingStyleFailure,
    CreateEditingStyleSuccess,
    useCreateEditingStyle
} from './create-editing-style/state/useCreateEditingStyle';
import { createErrorText } from './create-editing-style/state/createErrorText';
import { Translate } from '../../shared/LanguageService';
import FailureSnackbar from '../../shared/feedback/FailureSnackbar';
import SuccessSnackbar from '../../shared/feedback/SuccessSnackbar';

type EditingStylesPageProps = {
    editingStyles: EditingStyle[];
    loadFailed: boolean;
    onEditSave: (id: number, input: UpdateEditingStyleInput) => void;
    updateError: UpdateEditingStyleFailure | null;
    onDismissUpdateError: () => void;
    updateSuccess: UpdateEditingStyleSuccess | null;
    onDismissUpdateSuccess: () => void;
    onCreateSave: (input: CreateEditingStyleInput) => void;
    createError: CreateEditingStyleFailure | null;
    onDismissCreateError: () => void;
    createSuccess: CreateEditingStyleSuccess | null;
    onDismissCreateSuccess: () => void;
};

export function EditingStylesPage({
    editingStyles,
    loadFailed,
    onEditSave,
    updateError,
    onDismissUpdateError,
    updateSuccess,
    onDismissUpdateSuccess,
    onCreateSave,
    createError,
    onDismissCreateError,
    createSuccess,
    onDismissCreateSuccess
}: EditingStylesPageProps) {
    const { editingStyleToEdit, editDialogOpen, openEditDialog, closeEditDialog } =
        useEditEditingStyleModal();
    const { createDialogOpen, openCreateDialog, closeCreateDialog } = useCreateEditingStyleModal();

    return (
        <section>
            <Box
                sx={{
                    display: 'flex',

                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}
            >
                <h1>{Translate('editing-styles/list/heading')}</h1>
                <Button variant="contained" onClick={openCreateDialog}>
                    {Translate('editing-styles/list/button/add')}
                </Button>
            </Box>
            {loadFailed ? (
                <Alert severity="error" variant="filled">
                    {Translate('editing-styles/list/load-error')}
                </Alert>
            ) : (
                <EditingStylesList editingStyles={editingStyles} onEdit={openEditDialog} />
            )}
            <EditEditingStyleModal
                open={editDialogOpen}
                onClose={closeEditDialog}
                editingStyle={editingStyleToEdit}
                onEditSave={onEditSave}
            />
            <CreateEditingStyleModal
                open={createDialogOpen}
                onClose={closeCreateDialog}
                onCreateSave={onCreateSave}
            />
            <FailureSnackbar
                open={updateError !== null}
                onClose={onDismissUpdateError}
                text={updateErrorText(updateError)}
            />
            <SuccessSnackbar
                open={updateSuccess !== null}
                onClose={onDismissUpdateSuccess}
                text={
                    updateSuccess !== null
                        ? Translate('editing-styles/notification/update-succeeded', {
                              name: updateSuccess.name
                          })
                        : ''
                }
            />
            <FailureSnackbar
                open={createError !== null}
                onClose={onDismissCreateError}
                stackIndex={1}
                text={createErrorText(createError)}
            />
            <SuccessSnackbar
                open={createSuccess !== null}
                onClose={onDismissCreateSuccess}
                stackIndex={1}
                text={
                    createSuccess !== null
                        ? Translate('editing-styles/notification/create-succeeded', {
                              name: createSuccess.name
                          })
                        : ''
                }
            />
        </section>
    );
}

export default function EditingStylesPageDataLayer() {
    let { user } = useUserContext();
    let { logger } = useTelemetryContext();

    let { data, isError } = useQuery(
        [ListEditingStylesQueryKey],
        () => EditingStylesApi.getAllEditingStyles(),
        {
            onError: (error) => {
                logger.logException(error, {
                    origin: 'ListEditingStylesPage.DataLayer',
                    userInfo: getUserInfo(user)
                });
            }
        }
    );

    let { updateEditingStyle, updateError, dismissUpdateError, updateSuccess, dismissUpdateSuccess } =
        useUpdateEditingStyle();
    let { createEditingStyle, createError, dismissCreateError, createSuccess, dismissCreateSuccess } =
        useCreateEditingStyle();

    let editingStyles: EditingStyle[] = data ? data : [];

    return (
        <EditingStylesPage
            editingStyles={editingStyles}
            loadFailed={isError}
            onEditSave={updateEditingStyle}
            updateError={updateError}
            onDismissUpdateError={dismissUpdateError}
            updateSuccess={updateSuccess}
            onDismissUpdateSuccess={dismissUpdateSuccess}
            onCreateSave={createEditingStyle}
            createError={createError}
            onDismissCreateError={dismissCreateError}
            createSuccess={createSuccess}
            onDismissCreateSuccess={dismissCreateSuccess}
        />
    );
}
